/**
 * Accent adaptation — one user pick, correct in both modes.
 *
 * The accent replaces the design language's `signal` (Dialer Lab yellow):
 * the primary action's fill, the active-nav marker, focus and highlights.
 *
 * A single hex can't serve light and dark. `globals.css` already concedes
 * this: `signal-ink` is declared twice, same hue, different lightness. The
 * user-picked accent used to ignore that and apply one value to both, so a
 * dark pick like Slate (#334155, L≈0.36) all but vanished on a dark
 * background.
 *
 * So: keep the pick's HUE and CHROMA — its character — and clamp its
 * LIGHTNESS into a band that's readable per mode. Clamping rather than
 * forcing a fixed lightness means a muted choice stays muted and a vivid
 * one stays vivid; only unusable extremes get pulled in.
 *
 * Emitted as a `<style>` with `:root` and `.dark` blocks rather than an
 * inline style, so the cascade switches modes for us — no theme observer,
 * and no chance of the two getting out of sync.
 */

export type Oklch = { l: number; c: number; h: number }

/**
 * Light mode: most picks already land in a readable band, so clamp only
 * the extremes.
 */
const LIGHT_BAND = { min: 0.44, max: 0.62 }

/**
 * Dark mode: SHIFT, don't just clamp. Clamping alone pinned nearly every
 * accent to the floor — all of them below the band — which flattened
 * distinct colours to one lightness. Lifting by a fixed offset first
 * reproduces the hand-tuned `--c-*` dark values almost exactly:
 *
 *   blue   0.546 + 0.18 = 0.726   (--c-blue  dark: 0.72)
 *   teal   0.600 + 0.18 = 0.780   (--c-teal  dark: 0.78)
 *   green  0.596 + 0.18 = 0.776   (--c-green dark: 0.76)
 *   amber  0.666 + 0.18 → 0.820   (--c-amber dark: 0.82)
 *
 * The clamp then only catches genuine outliers (Slate 0.372 → 0.66).
 */
const DARK_LIFT = 0.18
const DARK_BAND = { min: 0.66, max: 0.82 }

/** Above this lightness an accent needs dark text on it, not white. */
const FOREGROUND_FLIP = 0.65

const clamp = (v: number, min: number, max: number) =>
  Math.min(max, Math.max(min, v))
const round = (n: number, dp = 3) => Math.round(n * 10 ** dp) / 10 ** dp

/** sRGB hex (`#rgb`, `#rrggbb`, `#rrggbbaa`) → OKLCH. */
export function hexToOklch(hex: string): Oklch | null {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.exec(hex.trim())
  if (!m) return null
  const raw = m[1] as string
  const full =
    raw.length === 3
      ? raw
          .split("")
          .map((c) => c + c)
          .join("")
      : raw.slice(0, 6)

  // sRGB → linear
  const [r, g, b] = [0, 2, 4].map((i) => {
    const v = parseInt(full.slice(i, i + 2), 16) / 255
    return v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4
  }) as [number, number, number]

  // linear → LMS → OKLab
  const l_ = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b)
  const m_ = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b)
  const s_ = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b)

  const L = 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_
  const A = 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_
  const B = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_

  const c = Math.sqrt(A * A + B * B)
  let h = (Math.atan2(B, A) * 180) / Math.PI
  if (h < 0) h += 360

  return { l: round(L, 4), c: round(c, 4), h: round(h, 2) }
}

/** The same accent, retargeted for one mode. */
export function adaptAccent(base: Oklch, mode: "light" | "dark"): Oklch {
  if (mode === "light") {
    return { ...base, l: round(clamp(base.l, LIGHT_BAND.min, LIGHT_BAND.max), 3) }
  }
  const lifted = base.l + DARK_LIFT
  return { ...base, l: round(clamp(lifted, DARK_BAND.min, DARK_BAND.max), 3) }
}

const fg = (l: number) => (l > FOREGROUND_FLIP ? "#0f1115" : "#ffffff")

/**
 * The readable twin (`--signal-ink`) must clear 4.5:1 as TEXT: on Day's
 * near-white that means dark, on Night's near-black, light.
 */
const INK_MAX_LIGHT = 0.5
const INK_MIN_DARK = 0.76

/**
 * CSS setting the design's `signal` family — `--signal`, `--on-signal`,
 * `--signal-ink`, `--signal-soft`, `--signal-edge` — per mode, or `null`
 * when there's no accent (fall through to the yellow in `globals.css`).
 * The shadcn `--primary` aliases follow automatically.
 *
 * Accepts a raw `oklch(...)` string too — used verbatim as the fill in
 * both modes, since we can't infer its parts without parsing and it's
 * assumed deliberate.
 */
export function accentCss(color: string | null | undefined): string | null {
  if (!color) return null

  // `!important` throughout: these rules land at the same `:root`
  // specificity as globals.css, so without it the winner is decided by
  // document order — and a <style> injected at runtime is not reliably
  // last (Vite re-injects the app stylesheet on HMR, for one). Dropping
  // it is why picking a colour appeared to do nothing.
  if (color.trim().toLowerCase().startsWith("oklch(")) {
    const decl =
      `--signal:${color} !important;--signal-ink:${color} !important;` +
      `--signal-soft:color-mix(in oklch, ${color} 14%, var(--surface)) !important;`
    return `:root,[data-theme]{${decl}}.dark{${decl}}`
  }

  const base = hexToOklch(color)
  if (!base) return null

  const build = (mode: "light" | "dark") => {
    const a = adaptAccent(base, mode)
    const fill = `oklch(${a.l} ${a.c} ${a.h})`
    const inkL =
      mode === "light"
        ? Math.min(a.l, INK_MAX_LIGHT)
        : Math.max(a.l, INK_MIN_DARK)
    const ink = `oklch(${round(inkL, 3)} ${a.c} ${a.h})`
    const soft =
      mode === "light"
        ? `oklch(0.965 ${round(Math.min(a.c, 0.12) * 0.3, 3)} ${a.h})`
        : `oklch(0.25 ${round(Math.min(a.c, 0.12) * 0.4, 3)} ${a.h})`
    return (
      `--signal:${fill} !important;` +
      `--on-signal:${fg(a.l)} !important;` +
      `--signal-ink:${ink} !important;` +
      `--signal-soft:${soft} !important;` +
      `--signal-edge:oklch(${round(Math.max(a.l - 0.25, 0.2), 3)} ${a.c} ${a.h} / 0.45) !important;`
    )
  }

  // `[data-theme=dark]` too: a Night-scoped subtree (the Spotlight) on a
  // Day page needs the dark-adapted fill to stay legible.
  return `:root,[data-theme=light]{${build("light")}}.dark,[data-theme=dark]{${build("dark")}}`
}

const STYLE_EL_ID = "vx-accent"

/** Apply (or clear) the accent via a single managed <style> tag. */
export function applyAccent(color: string | null | undefined): void {
  if (typeof document === "undefined") return
  const css = accentCss(color)
  let el = document.getElementById(STYLE_EL_ID) as HTMLStyleElement | null
  if (!css) {
    el?.remove()
    return
  }
  if (!el) {
    el = document.createElement("style")
    el.id = STYLE_EL_ID
    document.head.appendChild(el)
  }
  el.textContent = css
}
